<template>
  <div class="approval-list-table">
    <b-table :items="items" :fields="fields" bordered hover :busy="loading">
      <template v-slot:table-busy>
        <vcl-table :speed="5" :animate="true" :columns="10"></vcl-table>
      </template>

      <template v-slot:cell(code)="row">
        <div class="d-flex flex-column">
          <a class="font-weight-bold" v-bind:href="getLinkDoc(row.item)">
            {{ row.item.code }}
          </a>
          <span>{{ row.item.storeName }}</span>
        </div>
      </template>

      <template v-slot:cell(statusName)="row">
        <span
          class="status-badge"
          v-text="row.item.statusName"
          v-b-tooltip.hover
          :title="getStatusDescription(row.item.status)"
          :class="getStatusClass(row.item.status)"
        ></span>
        <span v-if="showCancelStatus(row.item)" class="d-block">
          ({{ getStatusCancelName(row.item.failReason) }})
        </span>
      </template>

      <template v-slot:cell(typeName)="row">
        <p>{{ row.item.typeName }}</p>
        <p class="text-primary">
          <a :href="getLinkDocEntity(row.item)" target="_blank">
            {{ row.item.entityId }}
          </a>
        </p>
      </template>

      <template v-slot:cell(customerName)="row">
        <p class="mb-0 font-weight-bold">
          {{ row.item.customerName }}
        </p>
        <p
          class="mb-0 medium"
          style="
            color: lightslategray;
            white-space: normal;
            font-size: 13px;
            font-weight: 500;
          "
        >
          {{ row.item.customerMobile }}
        </p>
        <div v-if="hasValue(row.item.customerBankName)">
          <b>NH:</b>
          {{ row.item.customerBankName }}
        </div>
        <div v-if="hasValue(row.item.customerBankNo)">
          <b>STK:</b>
          {{ row.item.customerBankNo }}
        </div>
        <div v-if="hasValue(row.item.customerAccountName)">
          <b>Tên TK:</b>
          {{ row.item.customerAccountName }}
        </div>
      </template>

      <template v-slot:cell(createdByName)="row">
        <p class="text-center">{{ row.item.createdByName }}</p>
        <p class="text-center">{{ row.item.createdAt }}</p>
      </template>

      <template v-slot:cell(actions)="row">
        <div class="text-center">
          <i
            class="fa fa-download text-primary cursor-pointer mb-4"
            aria-hidden="true"
            @click="$emit('open-file-modal', row.item)"
          ></i>
          <b-dropdown size="sm" id="dropdown-left" no-caret right>
            <template slot="button-content">
              <i class="flaticon2-settings"></i>
            </template>

            <b-dropdown-item
              @click="$emit('view-item', row.item)"
              v-if="!hasPermission('APPROVAL_UPDATE')"
            >
              <span class="dropdown-item-text">
                <i class="flaticon-eye"></i>
                &nbsp; Chi tiết
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="$emit('edit-item', row.item)"
              v-if="hasPermission('APPROVAL_UPDATE')"
            >
              <span class="dropdown-item-text">
                <i class="flaticon2-pen"></i>
                &nbsp; Chỉnh sửa
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="$emit('approve-item', row.item.id)"
              v-if="canApprove(row.item.status)"
            >
              <span class="dropdown-item-text">
                <i class="fa fa-angle-double-right"></i>
                &nbsp; Duyệt
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="$emit('change-status', row.item)"
              v-if="canChangeStatus(row.item.status)"
            >
              <span class="dropdown-item-text">
                <i class="fas fa-sync-alt icon-color"></i>
                &nbsp; Đổi trạng thái
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="$emit('return-request', row.item)"
              v-if="canReturnRequest(row.item.status)"
            >
              <span class="dropdown-item-text">
                <i class="flaticon2-reply-1"></i>
                &nbsp; Hoàn duyệt
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              @click="$emit('delete-item', row.item)"
              v-if="canDelete(row.item.status)"
              className="dropdown-item"
            >
              <span class="dropdown-item-text">
                <i class="flaticon2-rubbish-bin-delete-button text-danger"></i>
                &nbsp; Xóa
              </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { convertPrice } from '@/utils/common';
import { FAIL_REASON } from '@/utils/constants';
import {
  APPROVAL_STEP_STATUS,
  APPROVAL_ENTITY_TYPE,
  APPROVAL_TYPE_ENUM,
} from '@/utils/enum';
import localData from '@/utils/saveDataToLocal';
import { VclTable } from 'vue-content-loading';

export default {
  name: 'ApprovalListTable',
  components: {
    VclTable,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    failReasons: {
      type: Array,
      default: () => FAIL_REASON,
    },
  },
  data() {
    return {
      fields: [
        {
          key: 'code',
          label: 'Mã phiếu',
          thStyle: { fontWeight: 600, color: '#181c32', width: '15%' },
        },
        {
          key: 'typeName',
          label: 'Loại phiếu',
          thStyle: { fontWeight: 600, color: '#181c32', width: '15%' },
        },
        {
          key: 'amount',
          label: 'Số tiền',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
          tdClass: 'text-right',
          formatter: (value) => {
            return value ? convertPrice(value) : 0;
          },
        },
        {
          key: 'customerName',
          label: 'Khách hàng',
          thStyle: { fontWeight: 600, color: '#181c32', width: '20%' },
        },
        {
          key: 'paymentTypeName',
          label: 'Hình thức thanh toán',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
        },
        {
          key: 'statusName',
          label: 'Trạng thái',
          thStyle: { fontWeight: 600, color: '#181c32', width: '10%' },
        },
        {
          key: 'createdByName',
          label: 'Người tạo',
          thStyle: { fontWeight: 600, color: '#181c32', width: '15%' },
        },
        {
          key: 'actions',
          label: '',
        },
      ],
    };
  },
  methods: {
    /**
     * Check if user has permission
     */
    hasPermission(permission) {
      return localData.checkPermission(permission);
    },

    /**
     * Get link to approval details
     */
    getLinkDoc(item) {
      return `#/approval/${item.id}`;
    },

    /**
     * Get status description for tooltip
     */
    getStatusDescription(approvalStatus) {
      let description = '';
      switch (approvalStatus) {
        case APPROVAL_STEP_STATUS.TRANSFER_ORDER_INITIALTED:
          description =
            'Đã chuyển khoản và chờ xác nhận giao dịch từ phía ngân hàng';
          break;
        case APPROVAL_STEP_STATUS.PAID:
          description = 'Đã chuyển khoản thành công';
          break;
        case APPROVAL_STEP_STATUS.COMPLETED:
          description = 'Đã thanh toán thành công';
          break;
        case APPROVAL_STEP_STATUS.CHECKED:
          description = 'Phiếu hoàn tiền đã hậu kiểm thành công';
          break;
        case APPROVAL_STEP_STATUS.APPROVED:
          description = 'Phiếu đã được duyệt';
          break;
      }
      return description;
    },

    /**
     * Get CSS class for status badge
     */
    getStatusClass(approvalStatus) {
      let className = '';
      switch (approvalStatus) {
        case APPROVAL_STEP_STATUS.NEW: {
          className =
            'label font-weight-bold label-lg label-inline label-light-warning';
          break;
        }
        case APPROVAL_STEP_STATUS.APPROVED: {
          className = 'badge badge-success';
          break;
        }
        case APPROVAL_STEP_STATUS.CANCELED: {
          className = 'badge badge-dark';
          break;
        }
        case APPROVAL_STEP_STATUS.PAID: {
          className = 'badge badge-secondary';
          break;
        }
        case APPROVAL_STEP_STATUS.PAY_FAILED: {
          className = 'badge badge-warning';
          break;
        }
        case APPROVAL_STEP_STATUS.COMPLETED: {
          className = 'badge badge-info';
          break;
        }
        case APPROVAL_STEP_STATUS.ON_APPROVE: {
          className = 'badge badge-danger text-dark';
          break;
        }
        case APPROVAL_STEP_STATUS.CHECKED: {
          className = 'badge badge-warning text-white';
          break;
        }
        case APPROVAL_STEP_STATUS.TRANSFER_ORDER_INITIALTED: {
          className = 'badge badge-warning';
          break;
        }
      }
      return className;
    },

    /**
     * Check if cancel status should be shown
     */
    showCancelStatus(item) {
      return item.failReason && item.status === APPROVAL_STEP_STATUS.PAY_FAILED;
    },

    /**
     * Get cancel status name
     */
    getStatusCancelName(id) {
      const failReason = this.failReasons.find((reason) => reason.id == id);
      if (failReason) return failReason.name;
      return 'Chưa có lý do thanh toán KTC';
    },

    /**
     * Check if value exists and is not empty
     */
    hasValue(value) {
      return value && value.trim().length > 0;
    },

    /**
     * Check if user can approve the item
     */
    canApprove(status) {
      if (!this.hasPermission('APPROVAL_APPROVE')) {
        return false;
      }
      return (
        status === APPROVAL_STEP_STATUS.NEW ||
        status === APPROVAL_STEP_STATUS.ON_APPROVE
      );
    },

    /**
     * Check if user can change status
     */
    canChangeStatus(status) {
      if (!this.hasPermission('APPROVAL_UPDATE')) {
        return false;
      }
      const acceptStatusChanges = [
        APPROVAL_STEP_STATUS.APPROVED,
        APPROVAL_STEP_STATUS.PAID,
        APPROVAL_STEP_STATUS.COMPLETED,
        APPROVAL_STEP_STATUS.TRANSFER_ORDER_INITIALTED,
      ];
      return acceptStatusChanges.includes(status);
    },

    /**
     * Check if user can return request
     */
    canReturnRequest(status) {
      if (!this.hasPermission('APPROVAL_APPROVE')) {
        return false;
      }
      return (
        status === APPROVAL_STEP_STATUS.APPROVED ||
        status === APPROVAL_STEP_STATUS.ON_APPROVE
      );
    },

    /**
     * Check if user can delete the item
     */
    canDelete(status) {
      if (!this.hasPermission('APPROVAL_DELETE')) {
        return false;
      }
      return status === APPROVAL_STEP_STATUS.NEW;
    },

    getLinkDocEntity(item) {
      if (item.entityType === APPROVAL_ENTITY_TYPE.BILL) {
        if (item.type === APPROVAL_TYPE_ENUM.OVER_PAYMENT) {
          return `#/bills/update-bill?id=${item.entityId}`;
        } else if (item.type === APPROVAL_TYPE_ENUM.REFUND) {
          return `#/refund-bills/update-refund-bill?billNumber=${item.entityId}`;
        } else if (item.type === APPROVAL_TYPE_ENUM.VAT) {
          return `#/bills/update-bill?id=${item.entityId}`;
        }
      } else if (item.entityType === APPROVAL_ENTITY_TYPE.ORDER) {
        return `#/orders/edit-order/${item.entityId}`;
      } else if (item.entityType === APPROVAL_ENTITY_TYPE.STOCK_SLIP) {
        return `#/import-trade-in?code=${item.entityId}`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.approval-list-table {
  .cursor-pointer {
    cursor: pointer;
  }

  .dropdown-item-text {
    font-size: 12px;
    color: #3f4254;
  }

  .dropdown-item {
    padding: 0px;
  }

  .status-badge {
    width: max-content;
    display: inline-block;
  }

  // Fix CSS cho status "Mới"
  .label-light-warning {
    background-color: #fff3cd !important;
    color: #856404 !important;
    border-radius: 0.5rem;
    padding: 0.35rem 0.75rem !important;
    font-size: 0.875rem;
    font-weight: 600;
    display: inline-block;
    white-space: nowrap;
  }
}
</style>
